/**
 * Debug panel entry components with unified card layout.
 *
 * Every entry — stream events, grouped tool calls, and legacy types —
 * renders inside a shared `DebugCard` wrapper that provides:
 *   - Collapsed-by-default with chevron toggle
 *   - Consistent header: icon · label · preview · status · timestamp
 *   - Rich expanded content specific to each data type
 */

import React, { useState, useCallback } from 'react';
import {
  Wrench,
  Brain,
  ListTodo,
  AlertCircle,
  FileIcon,
  ChevronDown,
  ChevronRight,
  Zap,
  MessageSquare,
  Database,
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  Activity,
  LayoutDashboard,
  Upload,
  Circle,
} from 'lucide-react';
import { agentStyles } from '../DelegationBadge';
import type {
  DebugEntry as DebugEntryType,
  DebugEntryContent,
  StreamEventDebugEntry,
} from '../../types/chat';

// ============================================================================
// Grouped tool call type (exported for DebugSidebar)
// ============================================================================

export interface GroupedToolCall {
  type: 'grouped-tool-call';
  toolCallId: string;
  toolName: string;
  input: Record<string, unknown> | null;
  output: unknown | null;
  state: 'running' | 'completed' | 'failed';
  rawEvents: DebugEntryType[];
  timestamp: Date;
}

// ============================================================================
// Agent span type (exported for DebugSidebar)
// ============================================================================

export interface AgentSpan {
  type: 'agent-span';
  source: string;
  ancestry: string[];
  depth: number;
  startTime: Date;
  endTime?: Date;
  durationMs?: number;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    llmCalls: number;
    durationMs: number;
  };
  state: 'running' | 'completed' | 'failed';
  errorMessage?: string;
  progressEvents: Array<{
    message: string;
    timestamp: Date;
    detail?: Record<string, unknown>;
  }>;
  children: AgentSpan[];
}

export type EventTreeNode = AgentSpan | GroupedToolCall | DebugEntryType;

// ============================================================================
// Utilities
// ============================================================================

function formatTimestamp(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
    hour12: false,
  });
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function truncate(text: string, max: number): string {
  return text.length > max ? text.slice(0, max) + '...' : text;
}

// ============================================================================
// StructuredValue — recursive tree viewer for JSON data
// ============================================================================

function StructuredValue({ value, depth = 0 }: { value: unknown; depth?: number }) {
  const [expanded, setExpanded] = useState(depth < 1);

  if (value === null) return <span className="text-gray-400 italic">null</span>;
  if (value === undefined) return <span className="text-gray-400 italic">undefined</span>;

  if (typeof value === 'string') {
    const display = value.length > 120 ? value.slice(0, 120) + '...' : value;
    return <span className="text-green-600">"{display}"</span>;
  }
  if (typeof value === 'number') return <span className="text-blue-600">{value}</span>;
  if (typeof value === 'boolean') return <span className="text-amber-600">{value ? 'true' : 'false'}</span>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400">[]</span>;
    return (
      <div className="inline">
        <button onClick={() => setExpanded(!expanded)} className="text-gray-400 hover:text-gray-700">
          {expanded ? '▼' : '▶'} <span className="text-gray-400">[{value.length}]</span>
        </button>
        {expanded && (
          <div className="ml-3 border-l border-gray-200 pl-2">
            {value.map((item, i) => (
              <div key={i} className="py-0.5">
                <span className="text-gray-400 text-xs mr-1">{i}:</span>
                <StructuredValue value={item} depth={depth + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return <span className="text-gray-400">{'{}'}</span>;
    return (
      <div className="inline">
        <button onClick={() => setExpanded(!expanded)} className="text-gray-400 hover:text-gray-700">
          {expanded ? '▼' : '▶'} <span className="text-gray-400">{'{'}...{'}'}</span>
        </button>
        {expanded && (
          <div className="ml-3 border-l border-gray-200 pl-2">
            {entries.map(([key, val]) => (
              <div key={key} className="py-0.5">
                <span className="text-purple-600 text-xs">{key}</span>
                <span className="text-gray-400">: </span>
                <StructuredValue value={val} depth={depth + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return <span className="text-gray-600">{String(value)}</span>;
}

// ============================================================================
// Payload toggle — shows raw JSON at the bottom of expanded cards
// ============================================================================

function RawPayloadToggle({ data }: { data: unknown }) {
  const [show, setShow] = useState(false);
  return (
    <div className="mt-2 pt-2 border-t border-gray-100">
      <button
        onClick={() => setShow(!show)}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
      >
        {show ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <span>Raw payload</span>
      </button>
      {show && (
        <pre className="mt-1 text-xs text-gray-500 font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto bg-gray-50 rounded p-2">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ============================================================================
// DebugCard — shared collapsible card frame
// ============================================================================

interface DebugCardProps {
  icon: React.ReactNode;
  iconColor: string;
  label: string;
  preview?: React.ReactNode;
  timestamp: Date;
  statusIcon?: React.ReactNode;
  borderColor?: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

function DebugCard({
  icon,
  iconColor,
  label,
  preview,
  timestamp,
  statusIcon,
  borderColor = 'border-gray-200',
  children,
  defaultExpanded = false,
}: DebugCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className={`mb-1.5 rounded-lg border ${borderColor} bg-white overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-2.5 py-1.5 flex items-center gap-2 hover:bg-gray-50 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
        )}
        <span className={`flex-shrink-0 ${iconColor}`}>{icon}</span>
        <span className="text-xs font-medium text-gray-700 flex-shrink-0">{label}</span>
        {!expanded && preview && (
          <span className="text-xs text-gray-400 truncate flex-1 min-w-0">{preview}</span>
        )}
        {expanded && <span className="flex-1" />}
        {statusIcon && <span className="flex-shrink-0">{statusIcon}</span>}
        <span className="text-[10px] text-gray-400 font-mono flex-shrink-0 tabular-nums">
          {formatTimestamp(timestamp)}
        </span>
      </button>

      {expanded && (
        <div className="px-3 pb-2.5 pt-1.5 border-t border-gray-100 text-xs">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Stream event card props — maps eventType to icon, label, preview, color
// ============================================================================

interface StreamCardInfo {
  icon: React.ReactNode;
  iconColor: string;
  label: string;
  preview: string;
  borderColor?: string;
  statusIcon?: React.ReactNode;
}

function getStreamCardInfo(content: StreamEventDebugEntry): StreamCardInfo {
  const { eventType, payload } = content;

  switch (eventType) {
    // --- Text ---
    case 'text-delta':
    case 'text': {
      const text = String(payload.delta || payload.text || '');
      return {
        icon: <MessageSquare className="w-3 h-3" />,
        iconColor: 'text-sky-500',
        label: 'Text',
        preview: truncate(text, 80),
      };
    }

    // --- Reasoning ---
    case 'reasoning-delta':
    case 'reasoning': {
      const text = String(payload.delta || payload.text || '');
      return {
        icon: <Brain className="w-3 h-3" />,
        iconColor: 'text-amber-500',
        label: 'Reasoning',
        preview: truncate(text, 80),
      };
    }

    // --- Tool events (individual, not grouped) ---
    case 'tool-input-start': {
      const toolName = payload.toolName as string || 'unknown';
      return {
        icon: <Wrench className="w-3 h-3" />,
        iconColor: 'text-amber-500',
        label: toolName,
        preview: 'Starting...',
        statusIcon: <Loader2 className="w-3 h-3 animate-spin text-sky-500" />,
        borderColor: 'border-sky-200',
      };
    }
    case 'tool-input-available': {
      const toolName = payload.toolName as string || 'unknown';
      const args = payload.input ? JSON.stringify(payload.input) : '';
      return {
        icon: <Wrench className="w-3 h-3" />,
        iconColor: 'text-amber-500',
        label: toolName,
        preview: truncate(args, 60),
        borderColor: 'border-amber-200',
      };
    }
    case 'tool-output-available': {
      const toolName = payload.toolName as string || 'Result';
      const result = payload.output ? JSON.stringify(payload.output) : '';
      return {
        icon: <Wrench className="w-3 h-3" />,
        iconColor: 'text-green-500',
        label: toolName,
        preview: '→ ' + truncate(result, 60),
        statusIcon: <CheckCircle2 className="w-3 h-3 text-green-500" />,
        borderColor: 'border-green-200',
      };
    }

    // --- Data: Memory ---
    case 'data-memoryContext': {
      const memories = (payload.data as { memories?: unknown[] })?.memories;
      const count = memories?.length || 0;
      return {
        icon: <Database className="w-3 h-3" />,
        iconColor: 'text-cyan-500',
        label: 'Memory',
        preview: `${count} memor${count === 1 ? 'y' : 'ies'} loaded`,
      };
    }

    // --- Data: Plan ---
    case 'data-planStatus': {
      const data = payload.data as { strategy?: string; eventType?: string };
      return {
        icon: <ListTodo className="w-3 h-3" />,
        iconColor: 'text-yellow-500',
        label: 'Plan',
        preview: `${data?.strategy || '?'}: ${data?.eventType || '?'}`,
      };
    }

    // --- Data: Activity ---
    case 'data-activity': {
      const data = payload.data as { source?: string; message?: string; activityType?: string };
      return {
        icon: <Activity className="w-3 h-3" />,
        iconColor: 'text-emerald-500',
        label: data?.source || 'Activity',
        preview: truncate(data?.message || data?.activityType || '', 60),
      };
    }

    // --- Data: File events ---
    case 'data-fileAdded': {
      const data = payload.data as { filename?: string; path?: string };
      return {
        icon: <FileIcon className="w-3 h-3" />,
        iconColor: 'text-green-500',
        label: 'File Added',
        preview: data?.filename || data?.path || '',
      };
    }
    case 'data-fileDeleted': {
      const data = payload.data as { filename?: string; path?: string };
      return {
        icon: <FileIcon className="w-3 h-3" />,
        iconColor: 'text-red-500',
        label: 'File Deleted',
        preview: data?.filename || data?.path || '',
      };
    }

    // --- File upload ---
    case 'file-upload-complete': {
      const data = payload as { filename?: string; status?: string; error?: string };
      const ok = data.status === 'completed';
      return {
        icon: <Upload className="w-3 h-3" />,
        iconColor: ok ? 'text-green-500' : 'text-red-500',
        label: 'Upload',
        preview: data.filename || '',
        statusIcon: ok
          ? <CheckCircle2 className="w-3 h-3 text-green-500" />
          : <XCircle className="w-3 h-3 text-red-500" />,
        borderColor: ok ? 'border-green-200' : 'border-red-200',
      };
    }

    // --- Canvas events ---
    case 'canvas-update':
    case 'canvas-complete':
    case 'canvas-error': {
      const data = payload.data as { canvasId?: string; artifactId?: string } | undefined;
      return {
        icon: <LayoutDashboard className="w-3 h-3" />,
        iconColor: 'text-purple-500',
        label: 'Canvas',
        preview: data?.canvasId || eventType,
      };
    }

    // --- Error ---
    case 'error': {
      const msg = String(payload.errorText || payload.message || 'Unknown error');
      return {
        icon: <AlertCircle className="w-3 h-3" />,
        iconColor: 'text-red-500',
        label: 'Error',
        preview: truncate(msg, 80),
        borderColor: 'border-red-200',
      };
    }

    // --- Lifecycle ---
    case 'start':
    case 'start-step': {
      const meta = payload.metadata as { agent_name?: string } | undefined;
      return {
        icon: <Play className="w-3 h-3" />,
        iconColor: 'text-gray-400',
        label: eventType === 'start' ? 'Stream Start' : 'Step Start',
        preview: meta?.agent_name || '',
      };
    }
    case 'finish':
    case 'finish-step': {
      const meta = payload.metadata as { agent_name?: string; delegation?: { agent_name?: string } } | undefined;
      const agentName = meta?.delegation?.agent_name || meta?.agent_name;
      return {
        icon: <CheckCircle2 className="w-3 h-3" />,
        iconColor: agentName ? 'text-emerald-500' : 'text-gray-400',
        label: eventType === 'finish' ? 'Stream End' : 'Step End',
        preview: agentName ? `${agentName}` : '',
        statusIcon: agentName ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : undefined,
      };
    }

    default:
      return {
        icon: <Zap className="w-3 h-3" />,
        iconColor: 'text-gray-400',
        label: eventType,
        preview: '',
      };
  }
}

// ============================================================================
// Expanded content for stream events
// ============================================================================

function StreamEventExpanded({ content }: { content: StreamEventDebugEntry }) {
  const { eventType, payload } = content;

  switch (eventType) {
    case 'text-delta':
    case 'text': {
      const text = String(payload.delta || payload.text || '');
      return (
        <>
          <div className="bg-sky-50 border border-sky-100 rounded p-2 text-gray-700 whitespace-pre-wrap leading-relaxed">
            {text}
          </div>
          <RawPayloadToggle data={payload} />
        </>
      );
    }

    case 'reasoning-delta':
    case 'reasoning': {
      const text = String(payload.delta || payload.text || '');
      return (
        <>
          <div className="bg-amber-50 border border-amber-100 rounded p-2 text-gray-700 italic whitespace-pre-wrap leading-relaxed">
            {text}
          </div>
          <RawPayloadToggle data={payload} />
        </>
      );
    }

    case 'tool-input-start': {
      const toolName = payload.toolName as string;
      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin text-sky-500" />
            <span className="font-mono text-gray-700">{toolName}</span>
            <span className="text-gray-400">starting...</span>
          </div>
          <RawPayloadToggle data={payload} />
        </div>
      );
    }

    case 'tool-input-available': {
      const toolName = payload.toolName as string;
      const input = payload.input as Record<string, unknown> | undefined;
      return (
        <div className="space-y-1.5">
          <div className="font-mono text-gray-700 font-medium">{toolName}</div>
          {input && Object.keys(input).length > 0 && (
            <div>
              <div className="text-gray-400 mb-1">Arguments</div>
              <div className="font-mono">
                <StructuredValue value={input} />
              </div>
            </div>
          )}
          <RawPayloadToggle data={payload} />
        </div>
      );
    }

    case 'tool-output-available': {
      const toolName = payload.toolName as string;
      const output = payload.output;
      const isError = typeof output === 'object' && output !== null && 'error' in output;
      return (
        <div className="space-y-1.5">
          {toolName && <div className="font-mono text-gray-700 font-medium">{toolName}</div>}
          <div>
            <div className={`mb-1 ${isError ? 'text-red-500' : 'text-green-600'}`}>
              {isError ? 'Error' : 'Result'}
            </div>
            <div className="font-mono">
              <StructuredValue value={output} />
            </div>
          </div>
          <RawPayloadToggle data={payload} />
        </div>
      );
    }

    case 'data-memoryContext': {
      const memories = (payload.data as { memories?: Array<{ content?: string; key?: string }> })?.memories || [];
      return (
        <div className="space-y-1.5">
          <div className="text-gray-500">{memories.length} memor{memories.length === 1 ? 'y' : 'ies'} loaded</div>
          {memories.length > 0 && (
            <div className="space-y-1 max-h-48 overflow-auto">
              {memories.map((m, i) => (
                <div key={i} className="bg-cyan-50 border border-cyan-100 rounded px-2 py-1 text-gray-600">
                  {m.key && <span className="font-medium text-cyan-700 mr-1.5">{m.key}:</span>}
                  {truncate(m.content || JSON.stringify(m), 120)}
                </div>
              ))}
            </div>
          )}
          <RawPayloadToggle data={payload} />
        </div>
      );
    }

    case 'data-planStatus': {
      const data = payload.data as { strategy?: string; eventType?: string; status?: Record<string, unknown> } | undefined;
      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-3">
            <span className="text-gray-400">Strategy</span>
            <span className="font-medium text-gray-700">{data?.strategy || '—'}</span>
            <span className="text-gray-400">Event</span>
            <span className="font-medium text-gray-700">{data?.eventType || '—'}</span>
          </div>
          {data?.status && (
            <div>
              <div className="text-gray-400 mb-1">Status</div>
              <div className="font-mono">
                <StructuredValue value={data.status} />
              </div>
            </div>
          )}
          <RawPayloadToggle data={payload} />
        </div>
      );
    }

    case 'data-activity': {
      const data = payload.data as { source?: string; activityType?: string; message?: string; [key: string]: unknown } | undefined;
      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-3 flex-wrap">
            {data?.source && (
              <>
                <span className="text-gray-400">Source</span>
                <span className="font-medium text-emerald-700">{data.source}</span>
              </>
            )}
            {data?.activityType && (
              <>
                <span className="text-gray-400">Type</span>
                <span className="font-medium text-gray-700">{data.activityType}</span>
              </>
            )}
          </div>
          {data?.message && (
            <div className="bg-emerald-50 border border-emerald-100 rounded p-2 text-gray-700">
              {data.message}
            </div>
          )}
          <RawPayloadToggle data={payload} />
        </div>
      );
    }

    case 'data-fileAdded':
    case 'data-fileDeleted': {
      const data = payload.data as { filename?: string; path?: string; size?: number; mimeType?: string } | undefined;
      const isAdded = eventType === 'data-fileAdded';
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <span className={`font-medium ${isAdded ? 'text-green-600' : 'text-red-600'}`}>
              {isAdded ? '+' : '−'}
            </span>
            <span className="font-medium text-gray-700">{data?.filename || data?.path || 'unknown'}</span>
          </div>
          {data?.path && (
            <div className="text-gray-400 font-mono">{data.path}</div>
          )}
          <div className="flex items-center gap-3 text-gray-400">
            {data?.size !== undefined && <span>{formatFileSize(data.size)}</span>}
            {data?.mimeType && <span>{data.mimeType}</span>}
          </div>
        </div>
      );
    }

    case 'file-upload-complete': {
      const data = payload as { filename?: string; status?: string; error?: string };
      const ok = data.status === 'completed';
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {ok ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <XCircle className="w-3.5 h-3.5 text-red-500" />
            )}
            <span className="font-medium text-gray-700">{data.filename}</span>
          </div>
          {data.error && (
            <div className="text-red-600 bg-red-50 border border-red-100 rounded p-2">
              {data.error}
            </div>
          )}
        </div>
      );
    }

    case 'error': {
      const msg = String(payload.errorText || payload.message || 'Unknown error');
      return (
        <div className="text-red-700 bg-red-50 border border-red-100 rounded p-2">
          {msg}
        </div>
      );
    }

    case 'start':
    case 'finish':
    case 'start-step':
    case 'finish-step': {
      const meta = payload.metadata as Record<string, unknown> | undefined;
      const isFinish = eventType === 'finish' || eventType === 'finish-step';
      const agentName = (meta?.agent_name || (meta?.delegation as { agent_name?: string })?.agent_name) as string | undefined;
      return (
        <div className="space-y-1.5">
          {agentName && (
            <div className="flex items-center gap-2">
              {isFinish ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              ) : (
                <Loader2 className="w-3.5 h-3.5 text-sky-500" />
              )}
              <span className="font-medium text-gray-700">{agentName}</span>
            </div>
          )}
          {meta && Object.keys(meta).length > 0 && (
            <div>
              <div className="text-gray-400 mb-1">Metadata</div>
              <div className="font-mono">
                <StructuredValue value={meta} />
              </div>
            </div>
          )}
          {!meta && !agentName && (
            <div className="text-gray-400 italic">Lifecycle event</div>
          )}
        </div>
      );
    }

    default:
      return (
        <div className="font-mono">
          <StructuredValue value={payload} />
        </div>
      );
  }
}

// ============================================================================
// ToolCallCard — grouped tool call display (uses DebugCard)
// ============================================================================

export function ToolCallCard({ toolCall }: { toolCall: GroupedToolCall }) {
  const [showRawEvents, setShowRawEvents] = useState(false);
  const { toolName, input, output, state, rawEvents, timestamp } = toolCall;

  const stateIcon = {
    running: <Loader2 className="w-3 h-3 animate-spin text-sky-500" />,
    completed: <CheckCircle2 className="w-3 h-3 text-green-500" />,
    failed: <XCircle className="w-3 h-3 text-red-500" />,
  }[state];

  const borderColor = {
    running: 'border-sky-200',
    completed: 'border-green-200',
    failed: 'border-red-200',
  }[state];

  const getSummary = (): string => {
    if (state === 'running') return 'Running...';
    if (state === 'failed') return 'Failed';
    if (output === null) return 'Done';
    if (typeof output === 'object' && output !== null) {
      const keys = Object.keys(output);
      return keys.length <= 3 ? keys.join(', ') : `${keys.length} fields`;
    }
    return 'Done';
  };

  return (
    <DebugCard
      icon={<Wrench className="w-3 h-3" />}
      iconColor="text-amber-500"
      label={toolName}
      preview={`→ ${getSummary()}`}
      timestamp={timestamp}
      statusIcon={stateIcon}
      borderColor={borderColor}
    >
      {/* Arguments */}
      {input && Object.keys(input).length > 0 && (
        <div className="mb-2">
          <div className="text-gray-400 mb-1">Arguments</div>
          <div className="font-mono">
            <StructuredValue value={input} />
          </div>
        </div>
      )}

      {/* Result */}
      {state === 'completed' && output !== null && (
        <div className="mb-2">
          <div className="text-green-600 mb-1">Result</div>
          <div className="font-mono">
            <StructuredValue value={output} />
          </div>
        </div>
      )}
      {state === 'completed' && output === null && (
        <div className="text-green-600">Completed (no output)</div>
      )}
      {state === 'failed' && (
        <div className="mb-2">
          <div className="text-red-500 mb-1">Error</div>
          <div className="font-mono">
            <StructuredValue value={output} />
          </div>
        </div>
      )}
      {state === 'running' && (
        <div className="text-sky-600 flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" />
          Running...
        </div>
      )}

      {/* Raw events */}
      {rawEvents.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <button
            onClick={(e) => { e.stopPropagation(); setShowRawEvents(!showRawEvents); }}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showRawEvents ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <span>Raw events ({rawEvents.length})</span>
          </button>
          {showRawEvents && (
            <div className="mt-1 space-y-1">
              {rawEvents.map((entry) => (
                <div key={entry.id} className="p-1.5 rounded bg-gray-50 text-xs font-mono text-gray-500">
                  {(entry.content as StreamEventDebugEntry).eventType}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </DebugCard>
  );
}

// ============================================================================
// AgentSpanCard — collapsible agent lifecycle span (uses DebugCard)
// ============================================================================

const DEPTH_BORDER_COLORS = ['border-l-sky-300', 'border-l-amber-300', 'border-l-emerald-300', 'border-l-purple-300'];

const defaultAgentStyle = { color: 'text-gray-700', bgColor: 'bg-gray-50 border-gray-200' };

/** Dot color mapped from agentStyles — uses the text color class name to derive a fill color. */
function agentDotColor(source: string): string {
  const style = agentStyles[source];
  if (!style) return 'text-gray-400';
  // Reuse the text color from agentStyles (e.g. text-amber-700 → text-amber-500 for dot)
  return style.color.replace('-700', '-500');
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTokenCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

/** Progress timeline showing per-tool steps inside an agent span. */
function ToolProgressTimeline({
  events,
}: {
  events: AgentSpan['progressEvents'];
}) {
  // Pair "Running X" with "Completed X" by tool_name
  interface ToolStep {
    toolName: string;
    startTime: Date;
    endTime?: Date;
    durationMs?: number;
    completed: boolean;
  }

  const steps: ToolStep[] = [];
  const plainMessages: Array<{ message: string; timestamp: Date }> = [];
  const openTools = new Map<string, ToolStep>();

  for (const ev of events) {
    const toolName = ev.detail?.tool_name as string | undefined;

    if (!toolName) {
      plainMessages.push({ message: ev.message, timestamp: ev.timestamp });
      continue;
    }

    const msg = ev.message || '';
    const isComplete =
      msg.toLowerCase().startsWith('completed') ||
      msg.toLowerCase().startsWith('finished');

    if (!isComplete) {
      // Start of a tool
      const step: ToolStep = {
        toolName,
        startTime: ev.timestamp,
        completed: false,
      };
      openTools.set(toolName, step);
      steps.push(step);
    } else {
      // Completion of a tool
      const existing = openTools.get(toolName);
      if (existing) {
        existing.endTime = ev.timestamp;
        existing.durationMs =
          ev.timestamp.getTime() - existing.startTime.getTime();
        existing.completed = true;
        openTools.delete(toolName);
      } else {
        steps.push({
          toolName,
          startTime: ev.timestamp,
          endTime: ev.timestamp,
          completed: true,
        });
      }
    }
  }

  if (steps.length === 0 && plainMessages.length === 0) return null;

  return (
    <div className="space-y-1">
      {steps.map((step, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          {step.completed ? (
            <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
          ) : (
            <Loader2 className="w-3 h-3 animate-spin text-sky-500 flex-shrink-0" />
          )}
          <span className="font-mono text-gray-700">{step.toolName}</span>
          {step.durationMs !== undefined && (
            <span className="text-gray-400 ml-auto tabular-nums">
              {formatDuration(step.durationMs)}
            </span>
          )}
        </div>
      ))}
      {plainMessages.map((msg, i) => (
        <div key={`msg-${i}`} className="flex items-center gap-2 text-xs">
          <Circle className="w-2.5 h-2.5 text-gray-300 flex-shrink-0 ml-0.5" />
          <span className="text-gray-500">{msg.message}</span>
        </div>
      ))}
    </div>
  );
}

/** Toggle to show raw activity event detail payloads — mirrors ToolCallCard's "Raw events" pattern. */
function RawActivityEventsToggle({
  events,
}: {
  events: AgentSpan['progressEvents'];
}) {
  const [show, setShow] = useState(false);
  const eventsWithDetail = events.filter((e) => e.detail && Object.keys(e.detail).length > 0);
  if (eventsWithDetail.length === 0) return null;

  return (
    <div className="mt-2 pt-2 border-t border-gray-100">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShow(!show);
        }}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
      >
        {show ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <span>Raw events ({eventsWithDetail.length})</span>
      </button>
      {show && (
        <div className="mt-1 space-y-1">
          {eventsWithDetail.map((ev, i) => (
            <div key={i} className="p-1.5 rounded bg-gray-50 text-xs font-mono text-gray-500">
              <div className="text-gray-600 mb-0.5">{ev.message}</div>
              <StructuredValue value={ev.detail} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AgentSpanCard({ span }: { span: AgentSpan }) {
  const style = agentStyles[span.source] || defaultAgentStyle;
  const dotColor = agentDotColor(span.source);

  const stateIcon = {
    running: <Loader2 className="w-3 h-3 animate-spin text-sky-500" />,
    completed: <CheckCircle2 className="w-3 h-3 text-green-500" />,
    failed: <XCircle className="w-3 h-3 text-red-500" />,
  }[span.state];

  const borderColor = {
    running: 'border-sky-200',
    completed: 'border-green-200',
    failed: 'border-red-200',
  }[span.state];

  // Count tool steps for header summary
  const toolStepCount = span.progressEvents.filter(
    (e) => e.detail?.tool_name
  ).length;

  // Build preview parts
  const previewParts: string[] = [];
  if (span.ancestry.length > 1) {
    previewParts.push(span.ancestry.join(' → '));
  }
  if (span.durationMs !== undefined) {
    previewParts.push(formatDuration(span.durationMs));
  }
  if (span.usage) {
    previewParts.push(`${formatTokenCount(span.usage.totalTokens)} tokens`);
  }
  if (toolStepCount > 0) {
    previewParts.push(`${Math.ceil(toolStepCount / 2)} step${Math.ceil(toolStepCount / 2) !== 1 ? 's' : ''}`);
  }
  if (span.children.length > 0) {
    previewParts.push(`${span.children.length} sub-agent${span.children.length !== 1 ? 's' : ''}`);
  }

  const preview = previewParts.join('  ');

  return (
    <DebugCard
      icon={<Circle className={`w-2.5 h-2.5 fill-current ${dotColor}`} />}
      iconColor={dotColor}
      label={span.source}
      preview={preview}
      timestamp={span.startTime}
      statusIcon={stateIcon}
      borderColor={borderColor}
    >
      {/* Ancestry breadcrumb */}
      {span.ancestry.length > 1 && (
        <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
          {span.ancestry.map((agent, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span>→</span>}
              <span
                className={`font-medium ${
                  (agentStyles[agent] || defaultAgentStyle).color
                }`}
              >
                {agent}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Progress timeline */}
      {span.progressEvents.length > 0 && (
        <div className="mb-2">
          <div className="text-gray-400 mb-1">Progress</div>
          <ToolProgressTimeline events={span.progressEvents} />
        </div>
      )}

      {/* Usage */}
      {span.usage && (
        <div className="mb-2">
          <div className="text-gray-400 mb-1">Usage</div>
          <div className="text-xs text-gray-600">
            {formatTokenCount(span.usage.inputTokens)} in ·{' '}
            {formatTokenCount(span.usage.outputTokens)} out ·{' '}
            {span.usage.llmCalls} call{span.usage.llmCalls !== 1 ? 's' : ''}
            {span.usage.durationMs > 0 && (
              <> · {formatDuration(span.usage.durationMs)}</>
            )}
          </div>
        </div>
      )}

      {/* Duration (if no usage but duration is available) */}
      {!span.usage && span.durationMs !== undefined && (
        <div className="text-xs text-gray-400 mb-2">
          Duration: {formatDuration(span.durationMs)}
        </div>
      )}

      {/* Running state */}
      {span.state === 'running' && (
        <div className="text-sky-600 flex items-center gap-1.5 text-xs mb-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          Running...
        </div>
      )}

      {/* Error message */}
      {span.state === 'failed' && span.errorMessage && (
        <div className="mb-2">
          <div className="text-red-500 mb-1">Error</div>
          <div className="text-red-700 bg-red-50 border border-red-100 rounded p-2 text-xs">
            {span.errorMessage}
          </div>
        </div>
      )}

      {/* Raw activity events */}
      {span.progressEvents.length > 0 && (
        <RawActivityEventsToggle events={span.progressEvents} />
      )}

      {/* Child spans */}
      {span.children.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="text-gray-400 mb-1 text-xs">Sub-agents</div>
          <div className="space-y-1">
            {span.children.map((child) => {
              const depthIndex = (child.depth - 1) % DEPTH_BORDER_COLORS.length;
              const depthBorder = DEPTH_BORDER_COLORS[depthIndex];
              return (
                <div
                  key={`${child.source}-${child.startTime.getTime()}`}
                  className={`border-l-2 ${depthBorder}`}
                  style={{ paddingLeft: `${12 * (child.depth - span.depth)}px` }}
                >
                  <AgentSpanCard span={child} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </DebugCard>
  );
}

// ============================================================================
// DebugEntry — main entry component (uses DebugCard)
// ============================================================================

export function DebugEntry({ entry }: { entry: DebugEntryType }) {
  // --- Stream events (primary path) ---
  if (entry.type === 'stream-event') {
    const content = entry.content as StreamEventDebugEntry;
    const info = getStreamCardInfo(content);

    return (
      <DebugCard
        icon={info.icon}
        iconColor={info.iconColor}
        label={info.label}
        preview={info.preview}
        timestamp={entry.timestamp}
        statusIcon={info.statusIcon}
        borderColor={info.borderColor}
      >
        <StreamEventExpanded content={content} />
      </DebugCard>
    );
  }

  // --- Legacy: tool-call / tool-result ---
  if (entry.type === 'tool-call' || entry.type === 'tool-result') {
    const c = entry.content as { toolName: string; args: Record<string, unknown>; state: string; result?: unknown; duration?: number };
    const isResult = entry.type === 'tool-result';
    return (
      <DebugCard
        icon={<Wrench className="w-3 h-3" />}
        iconColor={isResult ? 'text-green-500' : 'text-sky-500'}
        label={c.toolName}
        preview={isResult ? '→ result' : 'call'}
        timestamp={entry.timestamp}
        borderColor={isResult ? 'border-green-200' : 'border-sky-200'}
      >
        {Object.keys(c.args).length > 0 && (
          <div className="mb-2">
            <div className="text-gray-400 mb-1">Arguments</div>
            <div className="font-mono"><StructuredValue value={c.args} /></div>
          </div>
        )}
        {c.result !== undefined && (
          <div className="mb-2">
            <div className={`mb-1 ${isResult ? 'text-green-600' : 'text-gray-400'}`}>Result</div>
            <div className="font-mono"><StructuredValue value={c.result} /></div>
          </div>
        )}
        {c.duration !== undefined && (
          <div className="text-gray-400">{c.duration}ms</div>
        )}
      </DebugCard>
    );
  }

  // --- Legacy: reasoning ---
  if (entry.type === 'reasoning') {
    const c = entry.content as { text: string; isStreaming: boolean };
    return (
      <DebugCard
        icon={<Brain className="w-3 h-3" />}
        iconColor="text-amber-500"
        label="Reasoning"
        preview={truncate(c.text, 60)}
        timestamp={entry.timestamp}
        statusIcon={c.isStreaming ? <Loader2 className="w-3 h-3 animate-spin text-amber-400" /> : undefined}
      >
        <div className="bg-amber-50 border border-amber-100 rounded p-2 text-gray-700 italic whitespace-pre-wrap leading-relaxed">
          {c.text}{c.isStreaming && '...'}
        </div>
      </DebugCard>
    );
  }

  // --- Legacy: plan-status ---
  if (entry.type === 'plan-status') {
    const c = entry.content as { strategy: string; eventType: string; status: Record<string, unknown> };
    return (
      <DebugCard
        icon={<ListTodo className="w-3 h-3" />}
        iconColor="text-yellow-500"
        label="Plan"
        preview={`${c.strategy}: ${c.eventType}`}
        timestamp={entry.timestamp}
      >
        <div className="space-y-1.5">
          <div className="flex items-center gap-3">
            <span className="text-gray-400">Strategy</span>
            <span className="font-medium text-gray-700">{c.strategy}</span>
            <span className="text-gray-400">Event</span>
            <span className="font-medium text-gray-700">{c.eventType}</span>
          </div>
          <div>
            <div className="text-gray-400 mb-1">Status</div>
            <div className="font-mono"><StructuredValue value={c.status} /></div>
          </div>
        </div>
      </DebugCard>
    );
  }

  // --- Legacy: file-event ---
  if (entry.type === 'file-event') {
    const c = entry.content as { eventType: 'file-added' | 'file-deleted'; filename?: string; path: string; size?: number; mimeType?: string };
    const isAdded = c.eventType === 'file-added';
    return (
      <DebugCard
        icon={<FileIcon className="w-3 h-3" />}
        iconColor={isAdded ? 'text-green-500' : 'text-red-500'}
        label={isAdded ? 'File Added' : 'File Deleted'}
        preview={c.filename || c.path}
        timestamp={entry.timestamp}
      >
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <span className={`font-medium ${isAdded ? 'text-green-600' : 'text-red-600'}`}>
              {isAdded ? '+' : '−'}
            </span>
            <span className="font-medium text-gray-700">{c.filename || c.path}</span>
          </div>
          <div className="text-gray-400 font-mono">{c.path}</div>
          <div className="flex items-center gap-3 text-gray-400">
            {c.size !== undefined && <span>{formatFileSize(c.size)}</span>}
            {c.mimeType && <span>{c.mimeType}</span>}
          </div>
        </div>
      </DebugCard>
    );
  }

  // --- Legacy: canvas-event ---
  if (entry.type === 'canvas-event') {
    const c = entry.content as { eventType: string; canvasId: string; artifactId: string; progress?: number; error?: string };
    return (
      <DebugCard
        icon={<LayoutDashboard className="w-3 h-3" />}
        iconColor="text-purple-500"
        label="Canvas"
        preview={`${c.canvasId} · ${c.eventType}`}
        timestamp={entry.timestamp}
        borderColor={c.error ? 'border-red-200' : undefined}
      >
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <span className="text-gray-400">Canvas</span>
            <span className="font-medium text-gray-700">{c.canvasId}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-400">Artifact</span>
            <span className="font-medium text-gray-700">{c.artifactId}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-400">Event</span>
            <span className="font-medium text-gray-700">{c.eventType}</span>
          </div>
          {c.progress !== undefined && (
            <div className="flex items-center gap-3">
              <span className="text-gray-400">Progress</span>
              <span className="font-medium text-gray-700">{Math.round(c.progress * 100)}%</span>
            </div>
          )}
          {c.error && (
            <div className="text-red-600 bg-red-50 border border-red-100 rounded p-2 mt-1">
              {c.error}
            </div>
          )}
        </div>
      </DebugCard>
    );
  }

  // --- Legacy: error ---
  if (entry.type === 'error') {
    const c = entry.content as { message: string; code?: number; recoverable: boolean };
    return (
      <DebugCard
        icon={<AlertCircle className="w-3 h-3" />}
        iconColor="text-red-500"
        label="Error"
        preview={truncate(c.message, 60)}
        timestamp={entry.timestamp}
        borderColor="border-red-200"
      >
        <div className="text-red-700 bg-red-50 border border-red-100 rounded p-2">
          {c.message}
        </div>
        <div className="flex items-center gap-3 mt-1.5 text-gray-400">
          {c.code !== undefined && <span>Code: {c.code}</span>}
          <span>{c.recoverable ? 'Recoverable' : 'Fatal'}</span>
        </div>
      </DebugCard>
    );
  }

  // --- Fallback ---
  return (
    <DebugCard
      icon={<Zap className="w-3 h-3" />}
      iconColor="text-gray-400"
      label={entry.type}
      timestamp={entry.timestamp}
    >
      <div className="font-mono">
        <StructuredValue value={entry.content} />
      </div>
    </DebugCard>
  );
}
